import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient } from '../utils/apiClient';

interface User {
  id: number;
  username: string;
  name: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing token on app start
    const savedToken = localStorage.getItem('auth_token');
    if (savedToken) {
      setToken(savedToken);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
      
      // Verify token with backend
      verifyToken(savedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  const verifyToken = async (tokenToVerify: string) => {
    try {
      const response = await apiClient.get('/api/auth/me');
      setUser(response.data.user);
      setIsLoading(false);
    } catch (error) {
      console.error('Token verification failed:', error);
      // Token is invalid, clear it
      localStorage.removeItem('auth_token');
      setToken(null);
      setUser(null);
      delete apiClient.defaults.headers.common['Authorization'];
      setIsLoading(false);
    }
  };

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      console.log('🔍 FRONTEND DEBUG: Attempting login with:', { username, password });
      
      const response = await apiClient.post('/api/auth/login', {
        username,
        password
      });

      console.log('🔍 FRONTEND DEBUG: Response:', response.data);

      if (response.data.success) {
        const { token: newToken, user: userData } = response.data;
        
        setToken(newToken);
        setUser(userData);
        
        // Save to localStorage
        localStorage.setItem('auth_token', newToken);
        
        // Set default authorization header
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('🔍 FRONTEND DEBUG: Login failed:', error);
      console.error('🔍 FRONTEND DEBUG: Error response:', (error as any).response?.data);
      return false;
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('auth_token');
    delete apiClient.defaults.headers.common['Authorization'];
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};